"use client";

import { useEffect, useMemo, useState } from "react";
import { userInitials } from "@/lib/user-display";

export type AvatarUser = {
  username: string;
  displayName?: string | null;
  image?: string | null;
  isDeleted?: boolean | null;
};

const sizeClass = {
  sm: "h-7 w-7 text-[10px]",
  md: "h-10 w-10 text-xs",
  lg: "h-16 w-16 text-lg",
} as const;

export function UserAvatar({
  user,
  size = "md",
  className = "",
}: {
  user: AvatarUser;
  size?: keyof typeof sizeClass;
  className?: string;
}) {
  const imageSrc = useMemo(() => {
    if (user.isDeleted) return null;
    const value = user.image?.trim();
    return value ? value : null;
  }, [user.image, user.isDeleted]);
  const [showImage, setShowImage] = useState(Boolean(imageSrc));

  useEffect(() => {
    setShowImage(Boolean(imageSrc));
  }, [imageSrc]);

  return (
    <span
      className={`relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full bg-[#273E47] font-black uppercase text-white ring-1 ring-black/5 ${sizeClass[size]} ${className}`}
      aria-hidden="true"
    >
      {imageSrc && showImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setShowImage(false)}
          className="h-full w-full object-cover"
        />
      ) : (
        userInitials(user)
      )}
    </span>
  );
}
